import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import uniqueId from 'lodash/fp/uniqueId';
import { usePopper } from 'react-popper';

import {
  Arrow,
  Box,
  TriggerIcon,
  TriggerIconActive,
  TriggerText,
  TriggerWrapper,
  Wrapper,
} from './Tooltip.styles';
import { IconHelp, IconHelpActive } from '../Icon';

export const Tooltip = ({ title, triggerText }) => {
  const [referenceElement, setReferenceElement] = useState(null);
  const [popperElement, setPopperElement] = useState(null);
  const [arrowElement, setArrowElement] = useState(null);
  const { styles: popperStyles, attributes, update } = usePopper(
    referenceElement,
    popperElement,
    {
      modifiers: [
        { name: 'arrow', options: { element: arrowElement } },
        { name: 'offset', options: { offset: [0, 10] } },
      ],
    }
  );
  const id = useMemo(() => uniqueId('tooltip-'), []);

  const updateTooltipPosition = () => {
    if (update) {
      update();
    }
  };

  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  return (
    <Wrapper
      data-testid="tooltip"
      onMouseOver={updateTooltipPosition}
      onFocus={updateTooltipPosition}
      onClick={handleClick}
      aria-describedby={id}
      aria-label={triggerText || 'help icon'}
      role="tooltip"
      tabIndex={0}
    >
      <TriggerWrapper
        ref={setReferenceElement}
        data-testid="tooltip-ref-element"
      >
        {triggerText ? (
          <TriggerText aria-hidden="true">{triggerText}</TriggerText>
        ) : (
          <>
            <TriggerIconActive data-testid="tooltip-trigger-icon-active">
              <IconHelpActive inline />
            </TriggerIconActive>
            <TriggerIcon data-testid="tooltip-trigger-icon">
              <IconHelp inline />
            </TriggerIcon>
          </>
        )}
      </TriggerWrapper>
      <Box
        ref={setPopperElement}
        style={popperStyles.popper}
        {...attributes.popper}
        id={id}
        role="tooltip"
      >
        {title}
        <Arrow ref={setArrowElement} style={popperStyles.arrow} />
      </Box>
    </Wrapper>
  );
};

Tooltip.displayName = 'Tooltip';

Tooltip.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  triggerText: PropTypes.string,
};

Tooltip.defaultProps = {
  title: '',
};

export default Tooltip;
